/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Ban,
  Download,
  AlertCircle,
  ChevronDown,
  Users,
  Building2,
  MapPin,
  RefreshCw,
  Plus,
} from "lucide-react";
import { Link } from "react-router-dom";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import { exportClientsToExcel } from "../../../utils/exportClientsToExcel";
import { toast } from "react-toastify";
import placeholderImage from "../../../assets/placeholder.webp";

const Clients = () => {
  const {
    clients,
    clientsPagination,
    getAllClients,
    toggleClientStatus,
    aToken,
  } = useContext(AdminContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [genderFilter, setGenderFilter] = useState("all");
  const [activeMenu, setActiveMenu] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showGenderDropdown, setShowGenderDropdown] = useState(false);

  // Refs for click-outside-to-close
  const statusDropdownRef = useRef(null);
  const genderDropdownRef = useRef(null);

  // Selection State
  const [selectedClients, setSelectedClients] = useState(new Set());

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Deactivate Modal
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [clientToDeactivate, setClientToDeactivate] = useState(null);

  // Refresh trigger — increment to force a re-fetch without adding getAllClients to deps
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch clients with server-side
  useEffect(() => {
    getAllClients({
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm,
      status: statusFilter,
      gender: genderFilter,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, searchTerm, statusFilter, genderFilter, refreshKey]);

  // Normalize clients data
  const normalizedClients = (clients || []).map((client) => ({
    id: client.id,
    name: client.name,
    address: {
      line1: client.addressLine1 || client.address?.line1 || "",
      line2: client.addressLine2 || client.address?.line2 || "",
    },
    status:
      client.status === "active" || client.status === "Active"
        ? "Active"
        : "Inactive",
    email: client.email,
    phoneNumber: client.phoneNumber,
    gender: client.gender,
    lastContact:
      client.lastContact ||
      (client.createdAt
        ? new Date(client.createdAt).toISOString().split("T")[0]
        : "N/A"),
    image: client.image,
  }));

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".client-action-menu")) return;
      setActiveMenu(null);

      // Close export menu if clicking outside
      if (!event.target.closest(".export-menu-container")) {
        setShowExportMenu(false);
      }

      // Close dropdowns if clicking outside
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
      if (
        genderDropdownRef.current &&
        !genderDropdownRef.current.contains(event.target)
      ) {
        setShowGenderDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedClients(new Set());
  }, [searchTerm, statusFilter, genderFilter]);

  const toggleMenu = (id) => {
    setActiveMenu(activeMenu === id ? null : id);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(normalizedClients.map((c) => c.id));
      setSelectedClients(allIds);
    } else {
      setSelectedClients(new Set());
    }
  };

  const handleSelectClient = (id) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedClients(newSelected);
  };

  const initiateDeactivate = (id) => {
    setClientToDeactivate(id);
    setShowDeactivateModal(true);
    setActiveMenu(null);
  };

  const confirmDeactivate = async () => {
    await toggleClientStatus(clientToDeactivate);
    // Increment refreshKey to trigger a single re-fetch
    setRefreshKey((k) => k + 1);
    setShowDeactivateModal(false);
    setClientToDeactivate(null);
  };

  const handleSearchSubmit = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setStatusFilter("All");
    setGenderFilter("all");
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setShowStatusDropdown(false);
    setCurrentPage(1);
  };

  const handleGenderFilter = (gender) => {
    setGenderFilter(gender);
    setShowGenderDropdown(false);
    setCurrentPage(1);
  };

  const isAllSelected =
    normalizedClients.length > 0 &&
    selectedClients.size === normalizedClients.length;

  // Get counts for stats from server-side data
  const activeCount = normalizedClients.filter(
    (c) => c.status === "Active",
  ).length;
  const inactiveCount = normalizedClients.filter(
    (c) => c.status === "Inactive",
  ).length;
  const maleCount = normalizedClients.filter(
    (c) => c.gender?.toLowerCase() === "male",
  ).length;
  const femaleCount = normalizedClients.filter(
    (c) => c.gender?.toLowerCase() === "female",
  ).length;

  // Export filtered data from server (current page view)
  const handleExportSelected = () => {
    const selectedClientsData = normalizedClients.filter((c) =>
      selectedClients.has(c.id),
    );
    if (selectedClientsData.length === 0) {
      toast.error("No clients selected");
      return;
    }
    exportClientsToExcel(selectedClientsData);
    setShowExportMenu(false);
  };

  const handleExportAll = async () => {
    try {
      // Fetch all clients without pagination
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_URL}/api/admin/clients/all?limit=10000`,
        {
          headers: {
            aToken,
          },
        },
      );
      const data = await response.json();

      if (data.success) {
        const allClients = (data.clients || []).map((client) => ({
          id: client.id,
          name: client.name,
          address: {
            line1: client.addressLine1 || client.address?.line1 || "",
            line2: client.addressLine2 || client.address?.line2 || "",
          },
          status:
            client.status === "active" || client.status === "Active"
              ? "Active"
              : "Inactive",
          email: client.email,
          phoneNumber: client.phoneNumber,
          gender: client.gender,
        }));

        if (allClients.length === 0) {
          toast.error("No clients to export");
          return;
        }

        exportClientsToExcel(allClients);
        toast.success(`Exported ${allClients.length} clients successfully`);
        setShowExportMenu(false);
      } else {
        toast.error(data.message || "Failed to fetch clients");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export all clients");
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-8 relative">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            Clients
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm sm:text-base">
            Manage your client base and view project statuses.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <div className="relative export-menu-container">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowExportMenu(!showExportMenu);
              }}
              className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 bg-card dark:bg-dark-surface text-gray-700 dark:text-dark-text border border-gray-200 dark:border-dark-border rounded-xl font-medium hover:bg-accent dark:hover:bg-gray-700 transition-colors shadow-sm"
            >
              <Download size={18} /> Export
              <ChevronDown
                size={16}
                className={`transition-transform ${showExportMenu ? "rotate-180" : ""}`}
              />
            </button>

            {/* Export Dropdown Menu */}
            {showExportMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {selectedClients.size > 0 && (
                  <button
                    onClick={handleExportSelected}
                    className="w-full text-left px-4 py-3 text-sm text-text dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3 border-b border-gray-100 dark:border-dark-border"
                  >
                    <Download size={16} className="text-primary" />
                    <div>
                      <p className="font-medium">Export Selected</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {selectedClients.size} client(s)
                      </p>
                    </div>
                  </button>
                )}
                <button
                  onClick={handleExportAll}
                  className="w-full text-left px-4 py-3 text-sm text-text dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-3"
                >
                  <Download size={16} className="text-primary" />
                  <div>
                    <p className="font-medium">Export All Clients</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      All {clientsPagination?.total || 0} clients
                    </p>
                  </div>
                </button>
              </div>
            )}
          </div>
          <Link
            to="/clients/add"
            className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-medium transition-colors shadow-sm"
          >
            <Plus size={18} /> Add Client
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-900 p-4 rounded-2xl shadow-sm border border-slate-800">
          <div className="flex items-center gap-3">
            <Users size={20} className="text-primary" />
            <div>
              <p className="text-xs text-gray-400">Total Clients</p>
              <p className="text-lg font-bold text-white">
                {clientsPagination?.total || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 p-4 rounded-2xl shadow-sm border border-slate-800">
          <div className="flex items-center gap-3">
            <Building2 size={20} className="text-primary" />
            <div>
              <p className="text-xs text-gray-400">Active</p>
              <p className="text-lg font-bold text-white">{activeCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 p-4 rounded-2xl shadow-sm border border-slate-800">
          <div className="flex items-center gap-3">
            <MapPin size={20} className="text-primary" />
            <div>
              <p className="text-xs text-gray-400">Inactive</p>
              <p className="text-lg font-bold text-white">{inactiveCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Toggle for Mobile */}
      <div className="lg:hidden mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full px-4 py-2.5 bg-card dark:bg-dark-surface rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border flex items-center justify-between hover:bg-accent dark:hover:bg-gray-700 transition-colors"
        >
          <span className="text-sm font-medium text-text dark:text-dark-text flex items-center gap-2">
            <Filter size={16} /> Filters & Search
          </span>
          <ChevronDown
            size={16}
            className={`text-primary transition-transform ${
              showFilters ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {/* Filter Bar */}
      <div
        className={`bg-card dark:bg-dark-surface p-1.5 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border mb-6 ${
          !showFilters && "lg:block hidden"
        } ${showFilters ? "block" : "hidden lg:block"}`}
      >
        <div className="flex flex-col lg:flex-row gap-2">
          {/* Search Bar */}
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted"
                size={16}
              />
              <input
                type="text"
                placeholder="Search by name, address, or phone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-accent dark:bg-gray-900 border border-transparent focus:bg-card dark:focus:bg-dark-surface focus:border-primary outline-none text-sm transition-all text-text dark:text-dark-text placeholder-gray-400"
              />
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearchSubmit}
              className="flex items-center justify-center px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-medium transition-colors"
            >
              <Search size={16} />
            </button>

            {/* Status Filter Dropdown */}
            <div className="relative" ref={statusDropdownRef}>
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
              >
                <Filter size={16} />
                {statusFilter === "All" ? "All Status" : statusFilter}
                <ChevronDown
                  size={14}
                  className={`transition-transform ${
                    showStatusDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showStatusDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                  {["All", "Active", "Inactive"].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusFilter(status)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        statusFilter === status
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {status === "All" ? "All Status" : status}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Gender Filter Dropdown */}
            <div className="relative" ref={genderDropdownRef}>
              <button
                onClick={() => setShowGenderDropdown(!showGenderDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
              >
                <Filter size={16} />
                {genderFilter === "all"
                  ? "All Genders"
                  : genderFilter.charAt(0).toUpperCase() +
                    genderFilter.slice(1)}
                <ChevronDown
                  size={14}
                  className={`transition-transform ${
                    showGenderDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showGenderDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                  {[
                    { value: "all", label: "All Genders" },
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleGenderFilter(option.value)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        genderFilter === option.value
                          ? "bg-primary text-white dark:bg-primary"
                          : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Reset Button */}
            <button
              onClick={handleResetFilters}
              className="flex items-center justify-center px-4 py-2 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Selection Actions Bar */}
      {selectedClients.size > 0 && (
        <div className="mb-4 px-4 sm:px-6 py-3 bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5 rounded-2xl border border-primary/20 dark:border-primary/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-primary dark:text-primary">
              {selectedClients.size} client(s) selected
            </span>
          </div>
          <div className="flex gap-2">
            <button className="px-3 sm:px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-medium transition-colors shadow-lg shadow-primary/20 dark:shadow-none">
              Bulk Actions
            </button>
          </div>
        </div>
      )}

      {/* Clients Table */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-visible">
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 dark:border-dark-border">
                <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-dark-muted uppercase tracking-wider w-10">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300 dark:border-gray-600 cursor-pointer"
                  />
                </th>
                <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Client Info
                </th>
                <th className="hidden lg:table-cell px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Address
                </th>
                <th className="px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="hidden md:table-cell px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Gender
                </th>
                <th className="hidden md:table-cell px-4 sm:px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 sm:px-6 py-4 text-right text-xs font-semibold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-4 sm:px-6 py-4 text-center text-xs font-semibold text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {normalizedClients.length > 0 ? (
                normalizedClients.map((client) => (
                  <tr
                    key={client.id}
                    className={`group hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors ${
                      selectedClients.has(client.id)
                        ? "bg-primary/5 dark:bg-primary/10"
                        : ""
                    }`}
                  >
                    <td className="px-4 sm:px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedClients.has(client.id)}
                        onChange={() => handleSelectClient(client.id)}
                        className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300 dark:border-gray-600 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setSelectedImage(client.image)}
                          className="focus:outline-none"
                        >
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-tr from-blue-100 to-primary/20 dark:from-blue-900/30 dark:to-primary/30 flex items-center justify-center">
                            <img
                              src={client.image || placeholderImage}
                              alt={client.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = placeholderImage;
                              }}
                            />
                          </div>
                        </button>
                        <div>
                          <p className="text-sm font-bold text-text dark:text-dark-text">
                            {client.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-dark-muted">
                            ID: {client.id.slice(-6)}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="hidden lg:table-cell px-4 sm:px-6 py-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-dark-muted">
                        {client.address.line1} {client.address.line2}
                      </p>
                    </td>
                    <td className="px-4 sm:px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          client.status === "Active"
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800"
                        }`}
                      >
                        {client.status}
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-4 sm:px-6 py-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-dark-muted">
                        {client.gender}
                      </p>
                    </td>
                    <td className="hidden md:table-cell px-4 sm:px-6 py-4">
                      <p className="text-sm text-gray-600 dark:text-dark-muted">
                        {client.email}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {client.lastContact}
                      </p>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-right">
                      <p className="text-sm font-bold text-text dark:text-dark-text">
                        {client.phoneNumber}
                      </p>
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-center relative client-action-menu">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMenu(client.id);
                        }}
                        className="p-2 text-gray-400 hover:text-primary dark:hover:text-primary hover:bg-white dark:hover:bg-gray-700 rounded-lg transition-all"
                      >
                        <MoreHorizontal size={18} />
                      </button>

                      {/* Dropdown */}
                      {activeMenu === client.id && (
                        <div className="absolute right-8 top-8 w-40 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                          <Link
                            to={`/clients/${client.id}`}
                            className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <Eye size={16} /> View Details
                          </Link>
                          <button
                            onClick={() => initiateDeactivate(client.id)}
                            className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 border-t border-gray-100 dark:border-dark-border ${
                              client.status === "Active"
                                ? "text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                : "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                            }`}
                          >
                            <Ban size={16} />
                            {client.status === "Active" ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                  >
                    No clients found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Component */}
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={clientsPagination?.totalPages || 1}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={clientsPagination?.total || 0}
          />
        </div>
      </div>

      {/* Deactivate Confirmation Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 text-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  normalizedClients.find((c) => c.id === clientToDeactivate)
                    ?.status === "Active"
                    ? "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
                    : "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
                }`}
              >
                <AlertCircle size={24} />
              </div>
              <h3 className="text-xl font-bold text-text dark:text-dark-text mb-2">
                {normalizedClients.find((c) => c.id === clientToDeactivate)
                  ?.status === "Active"
                  ? "Deactivate Client?"
                  : "Activate Client?"}
              </h3>
              <p className="text-gray-500 dark:text-dark-muted mb-6">
                {normalizedClients.find((c) => c.id === clientToDeactivate)
                  ?.status === "Active"
                  ? "Are you sure you want to deactivate this client? This action will restrict their access."
                  : "Are you sure you want to activate this client? This will restore their access."}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeactivateModal(false)}
                  className="flex-1 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-dark-text font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  No, Cancel
                </button>
                <button
                  onClick={confirmDeactivate}
                  className={`flex-1 px-4 py-2 rounded-xl text-white font-medium transition-colors ${
                    normalizedClients.find((c) => c.id === clientToDeactivate)
                      ?.status === "Active"
                      ? "bg-red-600 hover:bg-red-700 shadow-lg shadow-red-200 dark:shadow-none"
                      : "bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-200 dark:shadow-none"
                  }`}
                >
                  {normalizedClients.find((c) => c.id === clientToDeactivate)
                    ?.status === "Active"
                    ? "Yes, Deactivate"
                    : "Yes, Activate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl max-w-[80vw] max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage || placeholderImage}
              alt="Client"
              className="max-w-[80vw] max-h-[80vh] object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = placeholderImage;
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
